// ============================================================
// PLUMME — AI Advisor: operational report sections
// ============================================================
// Events, sales, customers, costs, suppliers, vouchers, open items, risk,
// actions, decisions and the data basis. Pure derivation, same rule as the
// financial sections: nothing is computed here that a model already computes.
//
// See aiAdvisorReportBlocks.jsx for the block contract these return.
const PlummeAiAdvisorReportOperations = (() => {
  const B = () => window.PlummeAiAdvisorReportBlocks;

  // ---------- Material events ----------
  // Reuses the advisor feed verbatim. The feed already answers "what changed in
  // the accounts", which is exactly what a board report's events section is,
  // and it already ships as { key, vars } with its own catalogue entries.
  const eventsSection = (ctx) => {
    const { safeArray, section, empty } = B();
    const feed = safeArray(ctx.advisor && ctx.advisor.feed);
    if (!feed.length) return section('hendelser', 'adv.rpt.sec.events', [empty('adv.rpt.empty.noEvents')]);
    return section('hendelser', 'adv.rpt.sec.events', [
      { type: 'list', items: feed.slice(0, 8).map((item) => ({ key: 'adv.feed.' + item.key + '.body', vars: item.vars || {} })) },
    ]);
  };

  // ---------- Sales and customers ----------
  const salesSection = (ctx) => {
    const { num, safeArray, round, round1, deltaTone, section, note, empty } = B();
    const sales = ctx.sales;
    const customers = safeArray(sales && sales.customers);
    if (!sales || (!num(sales.revenue) && !customers.length)) {
      return section('salg', 'adv.rpt.sec.sales', [
      sales?.nativeOnly ? note('info', 'adv.rpt.period.nativeSales') : null,empty('adv.rpt.empty.noSales', { year: ctx.year })]);
    }
    const kpi = sales.kpis || {};
    return section('salg', 'adv.rpt.sec.sales', [
      sales?.nativeOnly ? note('info', 'adv.rpt.period.nativeSales') : null,
      {
        type: 'kpis',
        items: [
          {
            id: 'revenue',
            label: { key: 'adv.rpt.kpi.revenue' },
            value: round(sales.revenue),
            format: 'currency',
            ...(kpi.revenueChangePct == null
              ? {}
              : { delta: round1(kpi.revenueChangePct), deltaTone: deltaTone(kpi.revenueChangePct, 'high'), compare: { key: ctx.period?.quarter ? 'adv.rpt.period.vsQuarter' : 'adv.rpt.cell.vsPreviousYear' } }),
          },
          { id: 'invoices', label: { key: 'adv.rpt.kpi.invoiceCount' }, value: round(sales.invoiceCount), format: 'number' },
          { id: 'avgInvoice', label: { key: 'adv.rpt.kpi.avgInvoice' }, value: round(kpi.avgInvoice), format: 'currency' },
          { id: 'customers', label: { key: 'adv.rpt.kpi.customerCount' }, value: round(sales.customerCount), format: 'number' },
        ],
      },
      customers.length
        ? {
          type: 'namedList',
          items: customers.slice(0, 4).map((customer, index) => ({
            id: customer.id || 'customer-' + index,
            name: customer.name,
            value: round(customer.revenue),
            format: 'currency',
          })),
        }
        : note('info', 'adv.rpt.note.noCustomerSplit'),
      kpi.topCustomerSharePct != null && kpi.topCustomerSharePct > 40
        ? note('warning', 'adv.rpt.note.concentration', { name: kpi.topCustomerName || '', pct: round1(kpi.topCustomerSharePct) })
        : null,
    ]);
  };

  const customerChangeSection = (ctx) => {
    const { round, round1, section, note, empty } = B();
    const kpi = (ctx.sales && ctx.sales.kpis) || {};
    const hasBasis = kpi.repeatRatePct != null || kpi.avgPaymentDays != null;
    if (!hasBasis) return section('nyetapte', 'adv.rpt.sec.customerChange', [empty('adv.rpt.empty.noCustomerHistory')]);
    return section('nyetapte', 'adv.rpt.sec.customerChange', [
      {
        type: 'metrics',
        items: [
          { id: 'repeat', label: { key: 'adv.rpt.metric.repeatRate' }, value: kpi.repeatRatePct == null ? null : round1(kpi.repeatRatePct), format: 'percent' },
          { id: 'paymentDays', label: { key: 'adv.rpt.metric.avgPaymentDays' }, value: kpi.avgPaymentDays == null ? null : round(kpi.avgPaymentDays), format: 'days' },
        ],
      },
      note('info', 'adv.rpt.note.customerBasis'),
    ]);
  };

  // ---------- Costs ----------
  // The advisor model already ranks the five largest cost lines with their
  // year-over-year change and flags the ones that jumped. Reusing it keeps the
  // report and the advisor's cost tab showing the same lines.
  const costsSection = (ctx) => {
    const { num, safeArray, round1, section, note, empty, money } = B();
    const costs = safeArray(ctx.advisor && ctx.advisor.costs).filter((line) => num(line.amount) > 0);
    if (!costs.length) return section('utvikling', 'adv.rpt.sec.costTrend', [empty('adv.rpt.empty.noCosts', { year: ctx.year })]);
    return section('utvikling', 'adv.rpt.sec.costTrend', [
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.account' }, { key: 'adv.rpt.col.amount' }, { key: 'adv.rpt.col.changeYoy' }],
        rows: costs.map((line) => [
          { text: line.name },
          money(line.amount),
          ctx.previousYearAvailable
            ? { value: round1(line.chg), format: 'percent', tone: line.flag ? 'danger' : null }
            : { key: 'adv.rpt.cell.noBasis' },
        ]),
      },
      ctx.previousYearAvailable && costs.some((line) => line.flag) ? note('warning', 'adv.rpt.note.costJump') : null,
    ]);
  };

  const suppliersSection = (ctx) => {
    const { safeArray, round, section, note, empty } = B();
    const suppliers = safeArray(ctx.partyInsights && ctx.partyInsights.suppliers);
    if (!suppliers.length) return section('leverandorer', 'adv.rpt.sec.suppliers', [empty('adv.rpt.empty.noSupplierSplit')]);
    return section('leverandorer', 'adv.rpt.sec.suppliers', [
      ctx.partyInsights?.nativeOnly ? note('info', 'adv.rpt.period.nativeSuppliers') : null,
      {
        type: 'namedList',
        items: suppliers.map((supplier, index) => ({ id: 'supplier-' + index, name: supplier.name, value: round(supplier.amount), format: 'currency' })),
      },
      note('info', 'adv.rpt.note.topFiveOnly'),
    ]);
  };

  // ---------- Vouchers to review ----------
  const voucherReviewSection = (ctx) => {
    const { num, round, section, text, note } = B();
    const followup = (ctx.home && ctx.home.followup) || {};
    const missing = num(followup.missingAttachmentCount);
    if (!missing) return section('gjennomgang', 'adv.rpt.sec.voucherReview', [note('success', 'adv.rpt.note.vouchersComplete')]);
    return section('gjennomgang', 'adv.rpt.sec.voucherReview', [
      text('adv.rpt.vouchers.missing', {
        count: missing,
        net: round(followup.missingAttachmentNet),
        vat: round(followup.missingAttachmentVat),
      }),
      note('warning', 'adv.rpt.note.vouchersRequired'),
    ]);
  };

  // ---------- Open items ----------
  const openItemsSection = (ctx, side) => {
    const { num, round, section, note, empty, money } = B();
    const overview = (ctx.home && ctx.home.overview) || {};
    const isSales = side === 'sales';
    const current = num(isSales ? overview.salesCurrent : overview.purchasesCurrent);
    const overdue = num(isSales ? overview.salesOverdue : overview.purchasesOverdue);
    const id = isSales ? 'kunde' : 'leverandor';
    const titleKey = isSales ? 'adv.rpt.sec.receivables' : 'adv.rpt.sec.payables';
    if (!current && !overdue) {
      return section(id, titleKey, [empty(isSales ? 'adv.rpt.empty.noReceivables' : 'adv.rpt.empty.noPayables')]);
    }
    return section(id, titleKey, [
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.status' }, { key: 'adv.rpt.col.amount' }],
        rows: [
          [{ key: 'adv.rpt.row.notDue' }, money(current)],
          [{ key: 'adv.rpt.row.overdue' }, { ...money(overdue), tone: overdue > 0 ? 'danger' : null }],
          [{ key: 'adv.rpt.row.total' }, money(current + overdue)],
        ],
      },
      overdue > 0 ? note('warning', isSales ? 'adv.rpt.note.overdueSales' : 'adv.rpt.note.overduePurchases', { amount: round(overdue) }) : null,
    ]);
  };

  // ---------- Risk ----------
  const risksSection = (ctx) => {
    const { safeArray, section, note, SEVERITY_BY_LEVEL } = B();
    const risks = safeArray(ctx.advisor && ctx.advisor.risks);
    if (!risks.length) return section('risiko', 'adv.rpt.sec.risk', [note('success', 'adv.rpt.note.noRisks')]);
    return section('risiko', 'adv.rpt.sec.risk', [
      {
        type: 'risks',
        items: risks.map((risk, index) => ({
          id: 'risk-' + index,
          severity: SEVERITY_BY_LEVEL[risk.level] || 'info',
          // The advisor model formats risk name and description for reading.
          // They are data here, not catalogue keys.
          title: { text: risk.name },
          body: { text: risk.desc },
        })),
      },
    ]);
  };

  // ---------- Recommended actions ----------
  // Actions are the advisor's own recommendations, carried with their recId so
  // "create task" writes back to the same recommendation-status store the
  // advisor page already uses. A report must not invent an action the advisor
  // has not made.
  const actionsSection = (ctx) => {
    const { safeArray, section, note, PRIORITY_BY_LEVEL } = B();
    const recommendations = safeArray(ctx.advisor && ctx.advisor.recommendations);
    if (!recommendations.length) return section('tiltak', 'adv.rpt.sec.actions', [note('success', 'adv.rpt.note.noActions')]);
    return section('tiltak', 'adv.rpt.sec.actions', [
      {
        type: 'actions',
        items: recommendations.map((rec, index) => ({
          id: 'action-' + index,
          recId: rec.id || null,
          priority: PRIORITY_BY_LEVEL[rec.lvl] || 'low',
          title: { text: rec.title || rec.text },
          impact: rec.impact ? { key: 'adv.rpt.action.impact', vars: { label: rec.impactLbl || '', value: rec.impact } } : undefined,
          // Carried raw as well: creating a task fingerprints the figure the
          // advice was about, and a formatted label cannot serve as that pin.
          impactValue: rec.impact != null ? rec.impact : undefined,
          route: rec.route || null,
        })),
      },
    ]);
  };

  // ---------- Decisions for the board ----------
  // Derived strictly from findings that already exist: a critical risk and an
  // open opportunity are decisions someone has to take. Nothing is invented,
  // and the block carries the disclaimer that it is not a board protocol.
  const decisionsSection = (ctx) => {
    const { num, safeArray, round, section, note, empty } = B();
    const items = [];
    safeArray(ctx.advisor && ctx.advisor.risks)
      .filter((risk) => risk.level === 'hoy')
      .forEach((risk, index) => items.push({ id: 'decision-risk-' + index, label: { key: 'adv.rpt.decision.risk', vars: { name: risk.name } } }));
    safeArray(ctx.advisor && ctx.advisor.recommendations)
      .filter((rec) => rec.lvl === 'opp')
      .slice(0, 3)
      .forEach((rec, index) => items.push({ id: 'decision-opp-' + index, label: { key: 'adv.rpt.decision.opportunity', vars: { name: rec.title || rec.text } } }));
    if (num(ctx.advisor && ctx.advisor.freeCapital) > 0) {
      items.push({ id: 'decision-capital', label: { key: 'adv.rpt.decision.freeCapital', vars: { amount: round(ctx.advisor.freeCapital) } } });
    }
    if (!items.length) return section('beslutning', 'adv.rpt.sec.decisions', [empty('adv.rpt.empty.noDecisions')]);
    return section('beslutning', 'adv.rpt.sec.decisions', [
      note('warning', 'adv.rpt.note.notAProtocol'),
      { type: 'decisions', items },
    ]);
  };

  // ---------- Data basis ----------
  // Says what the report was built from and what it could not see. This is the
  // section that keeps the document honest, so it is never optional and never
  // empty.
  const dataBasisSection = (ctx, chipKeys) => {
    const { safeArray, section } = B();
    const meta = [
      { key: 'adv.rpt.basis.generated', vars: { date: ctx.generatedAt } },
      ctx.period?.quarter ? { key: 'adv.rpt.period.quarterBasis', vars: { from: ctx.period.from, to: ctx.period.to } } : { key: 'adv.rpt.basis.period', vars: { year: ctx.year, months: ctx.monthsElapsed } },
    ];
    if (!ctx.previousYearAvailable) meta.push({ key: 'adv.rpt.basis.noPreviousYear', vars: { year: ctx.year - 1 } });
    if (!ctx.plan || !ctx.plan.hasBudget) meta.push({ key: 'adv.rpt.basis.noBudget', vars: { year: ctx.year } });
    if (ctx.partyInsights && ctx.partyInsights.scope === 'none') meta.push({ key: 'adv.rpt.basis.noPartySplit' });
    if (ctx.partyInsights && ctx.partyInsights.limitedToTop) meta.push({ key: 'adv.rpt.basis.topFive' });
    meta.push({ key: 'adv.rpt.basis.bookedOnly' });
    return section('datagrunnlag', 'adv.rpt.sec.dataBasis', [
      { type: 'sources', chips: safeArray(chipKeys).map((key) => ({ key })), meta },
    ]);
  };

  return {
    eventsSection, salesSection, customerChangeSection, costsSection, suppliersSection,
    voucherReviewSection, openItemsSection, risksSection, actionsSection, decisionsSection, dataBasisSection,
  };
})();

Object.assign(window, { PlummeAiAdvisorReportOperations });
